import { useEffect, useState, useCallback } from 'react'
import { Award, Trophy, Radio, Sparkles, Medal, Crown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import PageHeader from '@/components/PageHeader'
import { composAPI } from '@/lib/api'

/* Overkoepelend overzicht van alle "compo's": toernooien, Kempenvision-shows
   en Image Compo's. De modules blijven hun eigen leven leiden, maar bij het
   bekend maken van een winnaar schrijven ze een rij in compo_results. Hier
   worden die samen getoond, plus een dwars-leaderboard. */

const SOORT = {
  tournament:   { label: 'Toernooi',     icon: Trophy,   kleur: '#E4C265' },
  kempenvision:{ label: 'Kempenvision', icon: Radio,    kleur: '#B4FF39' },
  imagecompo:   { label: 'Image Compo',  icon: Sparkles, kleur: '#C77BFF' },
}

const plaatsKleur = (p) => p === 1 ? '#E4C265' : p === 2 ? '#C0C0C0' : p === 3 ? '#CD7F32' : '#94A3B8'

export default function ComposPage() {
  const [results, setResults]   = useState([])
  const [board, setBoard]       = useState([])
  const [tab, setTab]           = useState('leaderboard')
  const [filter, setFilter]     = useState('')     // '' = alles
  const [error, setError]       = useState(null)

  const laad = useCallback(async () => {
    try {
      const params = filter ? { kind: filter } : {}
      const [r, l] = await Promise.all([
        composAPI.listResults(params).catch(() => ({ data: { results: [] } })),
        composAPI.leaderboard(params).catch(() => ({ data: { entries: [] } })),
      ])
      setResults(r.data?.results ?? [])
      setBoard(l.data?.entries ?? [])
      setError(null)
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }, [filter])

  useEffect(() => { laad() }, [laad])

  return (
    <div>
      <PageHeader
        icon={Award}
        title="Compo uitslagen"
        subtitle="Uitslagen en dwars-leaderboard over alle compo's — toernooien én shows"
      />

      {error && <p className="text-sm text-signal mb-3">{error}</p>}

      {/* Filter-strip per soort */}
      <div className="flex gap-2 flex-wrap mb-4">
        <FilterKnop actief={filter === ''} onClick={() => setFilter('')} label="Alle compo's" kleur="#B4FF39" />
        {Object.entries(SOORT).map(([k, s]) => (
          <FilterKnop key={k} actief={filter === k} onClick={() => setFilter(k)}
                      icon={s.icon} label={s.label} kleur={s.kleur} />
        ))}
      </div>

      {/* Tab-strip */}
      <div className="flex gap-2 mb-4 border-b border-border">
        <TabKnop actief={tab === 'leaderboard'} onClick={() => setTab('leaderboard')}
                 label="Leaderboard" telling={board.length} />
        <TabKnop actief={tab === 'results'} onClick={() => setTab('results')}
                 label="Recente uitslagen" telling={results.length} />
      </div>

      {tab === 'leaderboard' && <Leaderboard entries={board} />}
      {tab === 'results'     && <Uitslagen results={results} />}
    </div>
  )
}

/* ── Sub-componenten ───────────────────────────────────────────── */

function FilterKnop({ icon: Icon, label, kleur, actief, onClick }) {
  return (
    <button onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] border text-sm transition-colors ${
              actief ? 'border-acid bg-acid/10' : 'border-border text-muted-foreground hover:text-foreground hover:border-acid/40'
            }`}>
      {Icon && <Icon className="h-3.5 w-3.5" style={{ color: kleur }} />}
      {label}
    </button>
  )
}

function TabKnop({ label, telling, actief, onClick }) {
  return (
    <button onClick={onClick}
            className={`px-3 py-2 border-b-2 text-sm font-semibold transition-colors ${
              actief ? 'border-acid text-acid' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
      {label}
      <span className="ml-2 font-mono text-[10px] uppercase tracking-widest">· {telling}</span>
    </button>
  )
}

function Leaderboard({ entries }) {
  if (entries.length === 0) {
    return <Leeg tekst="Nog geen resultaten — zodra een compo een winnaar heeft, verschijnen ze hier." />
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-12">#</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Deelnemer</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Punten</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center w-14" title="Aantal compo's">Compo</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center w-14" title="1e plaats">🥇</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center w-14" title="2e plaats">🥈</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center w-14" title="3e plaats">🥉</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={`${e.user_id ?? 'team'}-${e.team_name ?? e.naam}-${i}`}
                  className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                <td className="px-3 py-2">
                  {i === 0 ? <Crown className="h-4 w-4 text-gold" /> :
                   i < 3   ? <Medal className="h-4 w-4" style={{ color: plaatsKleur(i + 1) }} /> :
                             <span className="text-muted-foreground font-mono text-xs">{i + 1}</span>}
                </td>
                <td className="px-3 py-2 font-semibold">{e.naam}</td>
                <td className="px-3 py-2 text-right font-bold text-acid tabular-nums">{e.punten ?? 0}</td>
                <td className="px-3 py-2 text-center tabular-nums text-muted-foreground">{e.aantal_compos}</td>
                <td className="px-3 py-2 text-center tabular-nums" style={{ color: e.goud   > 0 ? '#E4C265' : 'var(--muted-foreground)' }}>{e.goud || ''}</td>
                <td className="px-3 py-2 text-center tabular-nums" style={{ color: e.zilver > 0 ? '#C0C0C0' : 'var(--muted-foreground)' }}>{e.zilver || ''}</td>
                <td className="px-3 py-2 text-center tabular-nums" style={{ color: e.brons  > 0 ? '#CD7F32' : 'var(--muted-foreground)' }}>{e.brons || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function Uitslagen({ results }) {
  if (results.length === 0) return <Leeg tekst="Nog geen uitslagen om te tonen." />
  // Groepeer resultaten per bron (kind + source), zodat je per compo alle plaatsen ziet.
  const groepen = new Map()
  for (const r of results) {
    const key = `${r.kind}-${r.source_id ?? r.source_name}`
    if (!groepen.has(key)) groepen.set(key, { kind: r.kind, source_name: r.source_name, ended_at: r.ended_at, plaatsen: [] })
    groepen.get(key).plaatsen.push(r)
  }
  const lijst = [...groepen.values()].sort((a, b) => new Date(b.ended_at) - new Date(a.ended_at))

  return (
    <div className="space-y-3">
      {lijst.map((g, i) => {
        const S = SOORT[g.kind] || SOORT.tournament
        const Icon = S.icon
        return (
          <Card key={i} style={{ borderLeft: `3px solid ${S.kleur}` }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Icon className="h-4 w-4" style={{ color: S.kleur }} />
                <span>{g.source_name}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  · {S.label} · {new Date(g.ended_at).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid gap-2 sm:grid-cols-3">
                {g.plaatsen.sort((a, b) => a.place - b.place).map(p => (
                  <div key={p.id}
                       className="flex items-center gap-3 rounded-[4px] border border-border/40 bg-card px-3 py-2">
                    <div className="h-8 w-8 rounded-full grid place-items-center font-bold text-sm shrink-0"
                         style={{ background: `${plaatsKleur(p.place)}22`, color: plaatsKleur(p.place) }}>
                      {p.place}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">
                        {p.user_display || p.username || p.display_name || p.team_name}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {p.points > 0 ? `+${p.points} pt` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

function Leeg({ tekst }) {
  return (
    <Card>
      <CardContent className="p-8 text-center text-muted-foreground text-sm">
        {tekst}
      </CardContent>
    </Card>
  )
}
